import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, Link } from 'react-router-dom';
import { accessLocalStorage } from '../../../services';
import { getAllMessageConfigs, deleteMessageConfig } from '../../../services/messageApi';

import './MessageList.css';
import Loading from '../../../components/Loading/LoadingModule';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faTrash, faPlus, faEnvelope, faSearch } from '@fortawesome/free-solid-svg-icons';

function MessageList() {
  const [loading, setLoading] = useState(true);
  const [configs, setConfigs] = useState([]);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [configToDelete, setConfigToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const history = useHistory();

  const fetchConfigs = async () => {
    try {
      const dataUser = await accessLocalStorage.getUserLocalStorage();
      
      if (!dataUser || !dataUser.user) {
        history.push('/');
        return;
      }
      
      const tenant = dataUser.user.idtenant;
      
      const response = await getAllMessageConfigs(tenant);
      if (response && response.success && response.data) {
        setConfigs(response.data);
      } else {
        setConfigs([]);
      }
      setLoading(false);
    } catch (error) {
      console.error('Erro ao buscar configurações:', error);
      setConfigs([]);
      setLoading(false);
    }
  };

  const validateUserLogged = useCallback(async () => {
    const dataUser = await accessLocalStorage.getUserLocalStorage();
    if (!dataUser) {
      history.push('/');
      return;
    }
    
    await fetchConfigs();
  }, [history]);

  useEffect(() => {
    validateUserLogged();
  }, [validateUserLogged]);

  const handleEdit = (config) => {
    history.push(`/config/message/edit/${config.id}`);
  };

  const confirmDelete = (config) => {
    setConfigToDelete(config);
    setShowConfirmDelete(true);
  };

  const handleDelete = async () => {
    if (!configToDelete) return;

    try {
      const response = await deleteMessageConfig(configToDelete.id);
      if (response && response.success) {
        await fetchConfigs();
        setShowConfirmDelete(false);
        setConfigToDelete(null);
      } else {
        console.error('Erro ao deletar configuração:', response);
        alert(`Erro ao deletar configuração: ${response.error || 'Erro desconhecido'}`);
      }
    } catch (error) {
      console.error('Erro ao deletar configuração:', error);
      alert('Erro ao deletar configuração');
    }
  };

  const cancelDelete = () => {
    setShowConfirmDelete(false);
    setConfigToDelete(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('pt-BR');
  };

  const filteredConfigs = configs.filter(config => 
    config.context_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (config.description && config.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
    config.tenant.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) return <Loading />;

  return (
    <div className="message-list-container">
      <div className="message-list-header">
        <div className="message-list-title-wrapper">
          <div className="message-list-title-left">
            <svg className="message-list-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <div>
              <h1 className="message-list-title">Contextos de Mensagem</h1>
              <p className="message-list-subtitle">Configure contextos de mensagem com encaminhamentos e condições de tempo</p>
            </div>
          </div>
          <div className="message-list-actions">
            <Link to="/config/message/create" className="btn-new-message">
              <FontAwesomeIcon icon={faPlus} />
              Nova Configuração
            </Link>
          </div>
        </div>
      </div>

      <div className="message-search-section">
        <div className="search-field">
          <FontAwesomeIcon icon={faSearch} className="search-icon" />
          <input
            className="search-input"
            type="text"
            placeholder="Buscar por nome do contexto, descrição ou tenant..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {filteredConfigs.length === 0 ? (
        <div className="empty-state">
          <svg className="empty-state-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
          </svg>
          <div className="empty-state-title">Nenhuma configuração encontrada</div>
          <p className="empty-state-text">
            {searchTerm 
              ? 'Nenhum resultado para a busca realizada.'
              : 'Clique em "Nova Configuração" para criar seu primeiro contexto de mensagem.'}
          </p>
        </div>
      ) : (
        <div className="message-table-wrapper">
          <table className="message-table">
            <thead className="message-table-header">
              <tr>
                <th>ID</th>
                <th>Tenant</th>
                <th>Nome do Contexto</th>
                <th>Descrição</th>
                <th>Tipo de Destino</th>
                <th>Overflows</th>
                <th>Time Conditions</th>
                <th>Criado em</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredConfigs.map((config) => (
                <tr key={config.id}>
                  <td>
                    <strong>#{config.id}</strong>
                  </td>
                  <td>
                    <span className="tenant-badge">
                      {config.tenant}
                    </span>
                  </td>
                  <td>
                    <span className="context-badge">
                      {config.context_name}
                    </span>
                  </td>
                  <td>
                    <span style={{ fontSize: '0.85rem' }}>
                      {config.description || '-'}
                    </span>
                  </td>
                  <td>
                    <span className="type-badge">
                      {({
                        endpoint: 'Ramal',
                        queue: 'Fila',
                        external: 'Externo',
                        disa: 'DISA',
                        whatsapp: 'WhatsApp',
                        cycle: 'Ciclo',
                        hangup: 'Desligar',
                        stasis: 'App Stasis'
                      })[config.destination_type] || config.destination_type}
                    </span>
                  </td>
                  <td>
                    <span className="count-badge">
                      {config.overflow_count || 0}
                    </span>
                  </td>
                  <td>
                    <span className="count-badge">
                      {config.timecondition_count || 0}
                    </span>
                  </td>
                  <td>
                    <span style={{ fontSize: '0.85rem' }}>
                      {formatDate(config.created_at)}
                    </span>
                  </td>
                  <td>
                    <div className="table-actions">
                      <button
                        className="action-btn btn-edit-message"
                        title="Editar"
                        onClick={() => handleEdit(config)}
                      >
                        <FontAwesomeIcon icon={faPen} />
                      </button>
                      <button
                        className="action-btn btn-delete-message"
                        title="Deletar"
                        onClick={() => confirmDelete(config)}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showConfirmDelete && (
        <div className="delete-modal-overlay" onClick={cancelDelete}>
          <div className="delete-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="delete-modal-header">
              <h3 className="delete-modal-title">Confirmar Deleção</h3>
              <button className="modal-close-btn" onClick={cancelDelete}>×</button>
            </div>
            <div className="delete-modal-body">
              <p>Tem certeza que deseja deletar a configuração <strong>{configToDelete?.context_name}</strong>?</p>
              <div className="delete-modal-warning">
                <strong>Atenção:</strong> Esta ação não pode ser desfeita.
              </div>
            </div>
            <div className="delete-modal-footer">
              <button className="btn-modal btn-modal-cancel" onClick={cancelDelete}>
                Cancelar
              </button>
              <button className="btn-modal btn-modal-delete" onClick={handleDelete}>
                <FontAwesomeIcon icon={faTrash} />
                Deletar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MessageList;

